import {Action, ActionHackCtx, IAction, IActionMenu, IActionToggle} from "lib/commons/actions";
import {JSX} from "lib/commons/xml/dom";
import {POPUP} from "back/commons/widgets/popups";
import {IDepotCtx, IDepotResCtx, IDepotShortResCtx, IDirResType, InfoFocusRes, InfoUpdatePendingRes, IResType, JCidMetas, JPersistMetas, resPath} from "lib/store/res";
import {JNodeInfos, JNodeShortProps, URLTREE} from "lib/store/urlTree";
import {JDepotCidResult, ODepotCidRequest} from "lib/store/cid";
import {EStoreAspects, IDepotResUiEnv, IDepotShortResUiEnv} from "lib/store/depot";
import {IInfoBroker} from "lib/commons/infos";
import {UtSelector} from "back/store/dialogs/utSelector";
import {UtBrowserFetcher} from "back/store/views/utBrowser";
import "back/store/actions/actions_Perms";
import {OEditPrcIdentInit} from "back/store/dialogs/editPrcIdent";
import {OEditMetasResInit} from "back/store/dialogs/editMetasRes";
import {ERROR} from "lib/core/errorReport";
import {Desk} from "lib/commons/desk";
import {IFolder} from "lib/commons/io/files";
import {isRespError} from "lib/commons/io/io";


export abstract class ResEditAction<C extends IDepotShortResCtx | IDepotResCtx> extends Action<C> {

	static getUiCtx(ctx: IDepotShortResCtx | IDepotResCtx | IDepotCtx, ev?: Event) {return ctx.emitter || (ev?.target as Node) || ctx.reg.env.uiRoot}

	static getInfoBroker(ctx: IDepotShortResCtx | IDepotResCtx | IDepotCtx): IInfoBroker {return ctx.infoBroker || ctx.reg.env.infoBroker}

	/** Update synchrone des metas d'une res.  */
	static async sendCidUpdate(cidMetas: JCidMetas, ctx: IDepotShortResCtx | IDepotResCtx | IDepotCtx, uiCtx: Node, returnProps?: Array<keyof (JDepotCidResult & JPersistMetas)>): Promise<JDepotCidResult & JCidMetas> {
		const cid = ctx.reg.env.universe.cid;
		try {
			const promise = cid.syncSend<JCidMetas>({cidMetas, returnProps: returnProps ? returnProps.concat(ReturnProps) : ReturnProps});
			//Notifie le contexte graphique d'un update en cours.
			ResEditAction.getInfoBroker(ctx)?.dispatchInfo(new InfoUpdatePendingRes(cidMetas.path, promise), uiCtx);
			const result = await promise;
			if (result.scCidSessStatus !== "commited") {
				ERROR.showError({msg: cid.extractCidLocalizedErrorMsg(result), cause: null, ctx: null, adminDetails: JSON.stringify(result)});
			}
			return result;
		} catch (e) {
			if (isRespError(e) && e.response.status === 403) {
				ERROR.report("Vous ne disposez pas des autorisations nécessaires pour effectuer cette modification.", e);
			} else {
				ERROR.report("Une erreur est survenue durant cette modification.", e);
			}
			throw e;
		}
	}

	showEvenIfMoved: boolean;

	isVisible(ctx: C): boolean {
		if (!this.showEvenIfMoved && ctx.reg.env.nodeInfos.t === "moved") return false;
		//En VCS aucune édition si le node n'est pas le head.
		if (ctx.reg.env.resType.prcVersionning === "VCS" && (ctx.reg.env.nodeInfos as JNodeInfos).succ?.length > 0) return false;
		return super.isVisible(ctx);
	}
}

/**
 * Ajoute une ressource au dossier courant ou en frère de la res courante.
 */
export class AddByImport<C extends IDepotShortResCtx | IDepotResCtx> extends ResEditAction<C> {

	/**
	 * Edition des metas d'une ressource en construction.
	 * @param metasIn Doit a minima contenir le processing et le path (cf EditPrcIdent).
	 */
	static async editMetas(metasIn: JCidMetas, ctx: IDepotShortResCtx | IDepotResCtx, uiCtx: Node): Promise<JCidMetas | null> {
		const resType = ctx.reg.env.resTypes.getResTypeByPrc(metasIn.processing);
		const editMetasCode = resType.reg.getPref("editMetas.beforeUpload", "editMetas");
		if (!editMetasCode || !resType.resForm(editMetasCode)) return metasIn;

		const EditMetasRes = (await import("back/store/dialogs/editMetasRes.js")).EditMetasRes;
		const metasOut = Object.assign({}, metasIn);

		//Construction du reg dédié au resType cible.
		const reg = ctx.reg.env.universe.newDepotResUiRegInCreation(ctx.reg, metasIn.path, resType);
		if (metasIn.olderResId != null && resType.prcVersionning !== "VCB") { //FIXME resType.prcVersionning !== "VCB" à supprimer quand formIdent OK.
			//On a un contenu existant.
			reg.env.nodeInfos = await ctx.reg.env.universe.adminUrlTree.nodeInfos(metasIn.path);
			//Affectation de la version exacte utilisée pour initialiser les metas pour le controle d'édition concurrente.
			metasOut.olderResId = reg.env.nodeInfos.resId;
		}

		const ct = new EditMetasRes().initialize({
			reg,
			skinOver: "webzone:panel",
			defaultValues: metasIn,
			formView: editMetasCode,
		} as OEditMetasResInit);
		const metas = await POPUP.showDialog<JCidMetas>(ct, uiCtx, {titleBar: "Édition des métadonnées...", resizer: {}, initWidth: "min(60em, 90vw)", initHeight: "min(40em, 90vh)"}).onNextClose();
		if (metas) {
			Object.assign(metasOut, metas);
			return metasOut;
		}
		return null;
	}

	static async pickFiles(multiple?: boolean): Promise<FileList> {
		//FIXME utiliser https://wicg.github.io/file-system-access/#api-showopenfilepicker.
		const input = document.createElement('input');
		input.type = "file";
		if (multiple) input.multiple = true;
		input.click();
		return new Promise<FileList>((resolve) => {
			input.onchange = function (this: HTMLInputElement) {
				resolve(this.files.length > 0 ? this.files : null);
			}
		});
	}

	constructor(id?: string) {
		super(id || 'addByImport');
		this._label = "Ressource";
		this._description = "Ajouter une ressource";
		this._icon = "/@skin@/store/actions/addRes.svg";
		this._group = "edit";
		this._enablePerms = "action.store#add.res";
	}

	/** Pour exploitation de cette action par darg/drop. */
	setFiles(files: FileList | (File | IFolder)[]): this {
		this.files = files;
		return this;
	}

	protected files: FileList | (File | IFolder)[];

	async execute(ctx: C, ev?: Event) {
		const uiCtx = AddByImport.getUiCtx(ctx, ev);
		const libUi = import("back/store/dialogs/editPrcIdent.js");
		const files = this.files || await AddByImport.pickFiles(true);
		if (files) {
			const EditPrcIdent = (await libUi).EditPrcIdent;
			//console.log("AddRes:::", files, ctx);
			const folderPath = ctx.reg.env.resType.prcIsFolder ? ctx.reg.env.nodeInfos.permaPath : URLTREE.extractParentPath(ctx.reg.env.nodeInfos.permaPath);
			for (let i = 0; i < files.length; i++) {
				if (!await this.importFileOrFolder(files[i], folderPath, ctx, uiCtx, i === files.length - 1, EditPrcIdent)) break;
			}
		}
	}

	async importFileOrFolder(file: File | IFolder, parentPath: resPath, ctx: C, uiCtx: Node, isLast: boolean, EditPrcIdent: typeof import("back/store/dialogs/editPrcIdent.js").EditPrcIdent) {
		if (file instanceof File) {
			const ct = new EditPrcIdent().initialize({
				reg: ctx.reg,
				skinOver: "webzone:panel",
				folderPath: parentPath,
				leafName: file.name,
				resTypesTree: {
					resTypes: ctx.reg.env.resTypes.getResTypesTree((resType) => !resType.prcIsNoContent && !resType.prcNoCreator)
				},
				preview: file
			} as OEditPrcIdentInit);
			let cidMetas = await POPUP.showDialog<JCidMetas>(ct, uiCtx, {titleBar: `Importer '${file.name}'`, resizer: {}, initWidth: "min(60em, 90vw)", initHeight: "min(40em, 90vh)"}).onNextClose();
			if (cidMetas) {
				cidMetas = await AddByImport.editMetas(cidMetas, ctx, uiCtx);
				if (cidMetas) {
					const cidResult = await ImportRes.doImport(file, cidMetas, ctx, uiCtx);
					if (cidResult?.scCidSessStatus === "commited") {
						if (isLast) ResEditAction.getInfoBroker(ctx)?.dispatchInfo(new InfoFocusRes(cidResult.path), uiCtx);
						return true;
					}
				}
			}
			//echec ou abandon
			return !isLast && await POPUP.confirm("Voulez-vous poursuivre l'import des fichiers restants ?", uiCtx, {cancelLbl: "Arrêter", okLbl: "Poursuivre"});
		} else {
			//Folder
			const result = await AddFolder.createFolder(parentPath, file.name, ctx, uiCtx, EditPrcIdent);
			if (!result || result.scCidSessStatus !== "commited") {
				if (!isLast) return await POPUP.confirm("Voulez-vous poursuivre l'import des fichiers restants ?", uiCtx, {cancelLbl: "Arrêter", okLbl: "Poursuivre"});
				return false;
			}
			if (file.entries.length > 0) {
				for (let i = 0; i < file.entries.length; i++) {
					if (!await this.importFileOrFolder(file.entries[i], result.path, ctx, uiCtx, isLast && i === file.entries.length - 1, EditPrcIdent)) return false;
				}
			} else {
				if (isLast) ResEditAction.getInfoBroker(ctx)?.dispatchInfo(new InfoFocusRes(result.path), uiCtx);
			}
		}
		return true;
	}
}


export class AddFolder<C extends IDepotShortResCtx | IDepotResCtx> extends ResEditAction<C> {

	static async createFolder(folderPath: resPath, defaultName: string, ctx: IDepotShortResCtx | IDepotResCtx, uiCtx: Node,
														EditPrcIdent: typeof import("back/store/dialogs/editPrcIdent.js").EditPrcIdent,
														restTypes?: (IDirResType | IResType)[]): Promise<JDepotCidResult & JCidMetas> {
		const ct = new EditPrcIdent().initialize({
			reg: ctx.reg,
			skinOver: "webzone:panel",
			folderPath,
			leafName: defaultName,
			resTypesTree: {
				resTypes: restTypes || ctx.reg.env.resTypes.getResTypesTree(resType => resType.prcIsFolder && !resType.prcNoCreator)
			},
			doBtnLabel: "Ajouter"
		} as OEditPrcIdentInit);
		let cidMetas = await POPUP.showDialog<JCidMetas | null>(ct, uiCtx, {titleBar: "Ajouter un dossier...", resizer: {}, initWidth: "min(60em, 90vw)", initHeight: "min(40em, 90vh)"}).onNextClose();
		if (cidMetas) {
			cidMetas = await AddByImport.editMetas(cidMetas, ctx, uiCtx);
			if (cidMetas) {
				try {
					const cid = ctx.reg.env.universe.cid;
					const cidResult = await cid.syncSend<JCidMetas>({cidMetas, returnProps: ReturnProps});
					if (cidResult.scCidSessStatus === "commited") {
						ResEditAction.getInfoBroker(ctx)?.dispatchInfo(new InfoFocusRes(cidResult.path), uiCtx);
					} else {
						await ERROR.showError({msg: cid.extractCidLocalizedErrorMsg(cidResult), cause: null, ctx: null, adminDetails: JSON.stringify(cidResult)});
					}
					return cidResult;
				} catch (e) {
					await ERROR.report("Une erreur est survenue durant la création de ce dossier.", e);
					throw e;
				}
			}
		}
		return null;
	}

	constructor(id?: string) {
		super(id || 'addFolder');
		this._label = "Dossier";
		this._description = "Ajouter un dossier";
		this._icon = "/@skin@/store/actions/addFolder.svg";
		this._group = "edit";
		this._enablePerms = "action.store#add.folder";
	}

	isVisible(ctx: C): boolean {
		if (!ctx.reg.env.resTypes.defaultFolderType) return false;
		return super.isVisible(ctx);
	}

	async execute(ctx: C, ev?: Event) {
		const uiCtx = AddFolder.getUiCtx(ctx, ev);
		const EditPrcIdent = (await import("back/store/dialogs/editPrcIdent.js")).EditPrcIdent;
		const folderPath = ctx.reg.env.resType.prcIsFolder ? ctx.reg.env.nodeInfos.permaPath : URLTREE.extractParentPath(ctx.reg.env.nodeInfos.permaPath);
		return AddFolder.createFolder(folderPath, "", ctx, uiCtx, EditPrcIdent);
	}
}

export class RenameRes<C extends IDepotShortResCtx | IDepotResCtx> extends ResEditAction<C> {

	constructor(id?: string) {
		super(id || 'renameRes');
		this._label = "Renommer...";
		this._description = "Renommer cette ressource";
		this._icon = "/@skin@/store/actions/renameRes.svg";
		this._group = "edit";
		this._enablePerms = "action.store#rename.res";
	}

	isVisible(ctx: C): boolean {
		if (URLTREE.isRootPath(ctx.reg.env.nodeInfos.permaPath)) return false; //root du dépot pas renommable.
		if (ctx.reg.env.resTypes.hasAnyVCB()) return false; //si VCB todo coté java...
		return super.isVisible(ctx);
	}

	async execute(ctx: C, ev?: Event) {
		const EditMetasRes = (await import("back/store/dialogs/editMetasRes.js")).EditMetasRes;
		const uiCtx = AddFolder.getUiCtx(ctx, ev);
		const resPath = ctx.reg.env.path;
		let reg = ctx.reg;
		try {
			if (reg.env.nodeInfos && !(reg.env.nodeInfos as JNodeInfos).metas) {
				//nodeInfos existe (on n'est pas en création) mais metas non chargées
				//reg IDepotShortResUiEnv -> IDepotResUiEnv
				reg = reg.env.universe.newDepotResUiRegFromDepotUiReg(reg, resPath);
			}
			const config: OEditMetasResInit = {
				reg: reg,
				skinOver: "webzone:panel",
				formIdent: {
					folderUi: "hidden",
					folderPath: URLTREE.extractParentPath(resPath),
					resUpdatable: "ifInTrash"
				}
				//showRedirOption: "unchecked" | "checked",
			};
			const ct = new EditMetasRes().initialize(config);
			await ct.initializedAsync;

			const cidMetas = await POPUP.showDialog<JCidMetas>(ct, uiCtx, {titleBar: `Identité de ${resPath}`, resizer: {}}).onNextClose();
			if (cidMetas != null) {
				try {
					const cid = ctx.reg.env.universe.cid;
					const infoBroker = ResEditAction.getInfoBroker(ctx); //mémoire car msg webSocket de suppr de la source peut kill ctx.reg au syncSend.
					const cidResult = await cid.syncSend<JCidMetas>({cidMetas, returnProps: ReturnProps});
					if (cidResult.scCidSessStatus === "commited") {
						infoBroker?.dispatchInfo(new InfoFocusRes(cidResult.path), uiCtx);
					} else {
						await ERROR.showError({msg: cid.extractCidLocalizedErrorMsg(cidResult), cause: null, ctx: null, adminDetails: JSON.stringify(cidResult)});
					}
					return cidResult;
				} catch (e) {
					await ERROR.report("Une erreur est survenue durant ce renommage.", e);
					throw e;
				}
			}
		} finally {
			if (reg !== ctx.reg) reg.close();
		}
	}
}

export class MoveRes<C extends IDepotShortResCtx | IDepotResCtx> extends ResEditAction<C> {

	constructor(id?: string) {
		super(id || 'moveRes');
		this._label = "Déplacer...";
		this._description = "Déplacer ce contenu";
		this._icon = "/@skin@/store/actions/moveRes.svg";
		this._group = "edit";
		this._enablePerms = "action.store#moveFrom.res";
	}

	isVisible(ctx: C): boolean {
		if (URLTREE.isRootPath(ctx.reg.env.nodeInfos.permaPath)) return false; //root du dépot pas déplaçable.
		if (ctx.reg.env.resTypes.hasAnyVCB()) return false; //si VCB todo coté java...
		return super.isVisible(ctx);
	}

	async execute(ctx: C, ev?: Event) {
		const fromPath = ctx.reg.env.nodeInfos.permaPath;
		const fromFolder = URLTREE.extractUnversionedPath(URLTREE.extractParentPath(fromPath));
		const uiCtx = MoveRes.getUiCtx(ctx, ev);
		const ct = new UtSelector().initialize({
			reg: ctx.reg,
			skinOver: "webzone:panel",
			initialPath: fromFolder,
			checkSel: (utSelector) => {
				const selPath = URLTREE.extractUnversionedPath(utSelector.currentSel?.permaPath);
				if (selPath === fromFolder) return "Sélectionnez le dossier cible du déplacement";
				if (selPath && URLTREE.isDescendantPathOrEqual(fromPath, selPath)) return "Un déplacement dans sa propre descendance est impossible";
				return true;
			},
			selBtnLabel: "Déplacer",
			utBrowser: {
				fetcher: new UtBrowserFetcher(ctx.reg.env.universe.adminUrlTree).setFilter("isFolder"),
			}
		});
		const name = URLTREE.extractUnversionedLeafName(fromPath);
		const folder = await POPUP.showDialog<JNodeShortProps>(ct, uiCtx, {titleBar: `Déplacer '${name}' vers...`, initWidth: "min(90vw, 40em)", initHeight: "min(90vh, 30em)"}).onNextClose();
		if (folder) {
			try {
				const cid = ctx.reg.env.universe.cid;
				const infoBroker = ResEditAction.getInfoBroker(ctx); //mémoire car msg webSocket de suppr de la source peut kill ctx.reg au syncSend.
				const cidResult = await cid.syncSend<JCidMetas>({
					cidMetas: {
						olderPath: fromPath,
						path: URLTREE.appendToPath(folder.permaPath, name)
					},
					returnProps: ReturnProps
				});
				if (cidResult.scCidSessStatus === "commited") {
					infoBroker?.dispatchInfo(new InfoFocusRes(cidResult.path), uiCtx);
				} else {
					await ERROR.showError({msg: cid.extractCidLocalizedErrorMsg(cidResult), cause: null, ctx: null, adminDetails: JSON.stringify(cidResult)});
				}
				return cidResult;
			} catch (e) {
				await ERROR.report("Une erreur est survenue durant le déplacement.", e);
				throw e;
			}
		}
	}
}


export class VisStateRes<C extends IDepotShortResCtx | IDepotResCtx> extends ResEditAction<C> implements IActionMenu<C> {
	constructor(id?: string) {
		super(id || 'visStateRes');
		this._group = "edit";
	}

	getDescription(ctx: C): string {
		return ctx.reg.env.resTypes.visStateDescription(ctx.reg.env.nodeInfos);
	}

	getLabel(ctx: C): string {
		return ctx.reg.env.resTypes.visStateLabel(ctx.reg.env.nodeInfos);
	}

	getIcon(ctx: C): string {
		return ctx.reg.env.resTypes.visStateIconUrl(ctx.reg.env.nodeInfos);
	}

	isVisible(ctx: C): boolean {
		if (!super.isVisible(ctx)) return false;
		if (URLTREE.isRootPath(ctx.reg.env.nodeInfos.permaPath)) return false; //XXX à voir si root versionné ?
		return ctx.reg.env.universe.hasAspect(EStoreAspects.trash) || ctx.reg.env.universe.hasAspect(EStoreAspects.unlisted);
	}

	isMenu(ctx: C): this is IActionMenu<C> {return true}

	getDatas(api: "menu", ctx: C): IAction<C>[] | any {
		if (api !== "menu") return;
		const actions: IAction<C>[] = [new VisStateVisible()];
		const u = ctx.reg.env.universe;
		if (u.hasAspect(EStoreAspects.unlisted)) actions.push(new VisStateLimited())
		if (u.hasAspect(EStoreAspects.trash)) actions.push(new VisStateInvisible())
		return actions;
	}

}

abstract class VisState<C extends IDepotShortResCtx | IDepotResCtx> extends ResEditAction<C> implements IActionToggle<C>, JNodeShortProps {

	trashed: boolean;
	unlisted: boolean;

	constructor(id?: string) {
		super(id);
		this._group = "edit";
		this.initState();
	}

	isToggle(ctx: C): this is IActionToggle<C> {return true}

	isEnabled(ctx: C): boolean {
		const perms = this.getPerms(ctx);
		if (perms && !this.checkObjectRootPerm(ctx, perms, true)) return false;
		return !this.getDatas("toggle", ctx);
	}

	getLabel(ctx: C): string {
		return ctx.reg.env.resTypes.visStateLabel(this);
	}

	getDescription(ctx: C): string {
		return ctx.reg.env.resTypes.visStateDescription(this);
	}

	getIcon(ctx: C): string {
		return ctx.reg.env.resTypes.visStateIconUrl(this);
	}

	abstract initState(): void;

	abstract getPerms(ctx: C): string | string[] | null;
}

export class VisStateVisible<C extends IDepotShortResCtx | IDepotResCtx> extends VisState<C> {

	initState() {
		this.trashed = false;
		this.unlisted = false;
	}

	getDatas(api: 'toggle', ctx: C): boolean | null {
		if (api !== "toggle") return;
		const ni = ctx.reg.env.nodeInfos;
		return !ni.trashed && !ni.unlisted;
	}

	async execute(ctx: C, ev?: Event) {
		const ni = ctx.reg.env.nodeInfos;
		return ResEditAction.sendCidUpdate({
			path: ctx.reg.env.nodeInfos.permaPath,
			olderResId: "",
			trashed: ctx.reg.env.universe.hasAspect(EStoreAspects.trash) || ni.trashed ? false : undefined,
			unlisted: ctx.reg.env.universe.hasAspect(EStoreAspects.unlisted) || ni.unlisted ? false : undefined,
		}, ctx, ResEditAction.getUiCtx(ctx, ev));
	}

	getPerms(ctx: C): string | string[] {
		const ni = ctx.reg.env.nodeInfos;
		if (ni.trashed) return ni.unlisted ? ["action.store#untrash.res", "action.store#relist.res"] : "action.store#untrash.res"
		return ni.unlisted ? "action.store#relist.res" : null
	}
}

export class VisStateLimited<C extends IDepotShortResCtx | IDepotResCtx> extends VisState<C> {

	initState() {
		this.trashed = false;
		this.unlisted = true;
	}

	getDatas(api: 'toggle', ctx: C): boolean | null {
		if (api !== "toggle") return;
		const ni = ctx.reg.env.nodeInfos;
		return !ni.trashed && ni.unlisted || false;
	}

	async execute(ctx: C, ev?: Event) {
		return ResEditAction.sendCidUpdate({
			path: ctx.reg.env.nodeInfos.permaPath,
			olderResId: "",
			trashed: ctx.reg.env.universe.hasAspect(EStoreAspects.trash) ? false : undefined,
			unlisted: true
		}, ctx, ResEditAction.getUiCtx(ctx, ev));
	}

	getPerms(ctx: C): string | string[] {
		return "action.store#unlist.res"
	}
}

export class VisStateInvisible<C extends IDepotShortResCtx | IDepotResCtx> extends VisState<C> {

	initState() {
		this.trashed = true;
		this.unlisted = false;
	}

	getDatas(api: 'toggle', ctx: C): boolean | null {
		if (api !== "toggle") return;
		return !!ctx.reg.env.nodeInfos.trashed;
	}

	async execute(ctx: C, ev?: Event) {
		return ResEditAction.sendCidUpdate({
			path: ctx.reg.env.nodeInfos.permaPath,
			olderResId: "",
			trashed: true,
			unlisted: ctx.reg.env.universe.hasAspect(EStoreAspects.unlisted) ? false : undefined
		}, ctx, ResEditAction.getUiCtx(ctx, ev));
	}

	getPerms(ctx: C): string | string[] {
		return "action.store#trash.res"
	}
}

export class RemoveRes<C extends IDepotShortResCtx | IDepotResCtx> extends ResEditAction<C> {

	constructor(id?: string) {
		super(id || 'removeRes');
		this._description = "Supprimer définitivement cette ressource (restauration impossible)";
		this._icon = "/@skin@/store/actions/removeRes.svg";
		this._group = "edit";
		this._enablePerms = "action.store#remove.res";
		this.showEvenIfMoved = true;
	}

	isVisible(ctx: C): boolean {
		if (URLTREE.isRootPath(ctx.reg.env.nodeInfos.permaPath)) return false; //XXX à voir si root versionné ?
		return super.isVisible(ctx);
	}

	getLabel(ctx: C): string {
		if (!this._label) return ctx.reg.env.resType.prcVersionning === "VCB" ? "Supprimer définitivement cette version" : "Supprimer définitivement";
		return super.getLabel(ctx);
	}

	async execute(ctx: C, ev?: Event) {
		const uiCtx = ResEditAction.getUiCtx(ctx, ev);
		if (await POPUP.confirm("Supprimer définitivement cette ressource ? Attention, aucune restauration ne sera possible.", uiCtx, {okLbl: "Supprimer définitivement"})) {
			return ResEditAction.sendCidUpdate({
				path: ctx.reg.env.nodeInfos.permaPath,
				action: ctx.reg.env.resType.prcVersionning === "VCB" ? "removeNode" : "removeAll"
			}, ctx, uiCtx);
		}
	}
}

/** Action qui import un nouveau contenu sans éditiondes metas. TODO autre action qui combine ImportRes et EditMetasRes. */
export class ImportRes<C extends IDepotShortResCtx | IDepotResCtx> extends ResEditAction<C> {

	static async doImport(file: File, cidMetas: JCidMetas, ctx: IDepotShortResCtx | IDepotResCtx, uiCtx: Node) {
		const progress = POPUP.showProgress(uiCtx, "Import en cours...");
		try {
			const req: ODepotCidRequest<JCidMetas, JPersistMetas> = {
				cidMetas,
				body: file,
				createMetas: true,
				progress: progress.progressCallback,
				returnProps: ReturnProps
			};
			const cid = ctx.reg.env.universe.cid;
			const sess = cid.newSession();
			await sess.send(req);
			const titleBar = progress.windowBar?.barLabel;
			if (titleBar) titleBar.label = "Traitement en cours...";
			progress.onProgress(1, undefined);
			const promise = sess.waitForEndSession<Pick<JPersistMetas, 'resId' | 'path' | 'processing'>>(ReturnProps, 750, (resp) => {
				const details = resp?.scCidSessDetails;
				if (details) {
					let done = 0;
					let total = 0;
					for (let step of details) {
						if (step.type.startsWith("step")) {
							total++;
							if (step.type === "stepDone") done++;
						}
					}
					progress.onProgress(done, total);
				}
			});
			ResEditAction.getInfoBroker(ctx)?.dispatchInfo(new InfoUpdatePendingRes(cidMetas.path, promise), ctx.emitter);
			const cidResult = await promise;
			if (cidResult.scCidSessStatus !== "commited") {
				await ERROR.showError({msg: cid.extractCidLocalizedErrorMsg(cidResult), cause: null, ctx: null, adminDetails: JSON.stringify(cidResult)});
			}
			return cidResult;
		} catch (e) {
			await ERROR.report("Une erreur est survenue durant l'envoi de ce contenu.", e);
			throw e;
		} finally {
			progress.close();
		}
	}

	constructor(id?: string) {
		super(id || 'importRes');
		this._label = "Importer...";
		this._description = "Importer un nouveau contenu pour cette ressource";
		this._icon = "/@skin@/store/actions/importRes.svg";
		this._group = "edit";
		this._enablePerms = "action.store#import.res";
	}

	isVisible(ctx: C): boolean {
		if (ctx.reg.env.resType.prcIsNoContent) return false;
		return super.isVisible(ctx);
	}

	async execute(ctx: C, ev?: Event) {
		const uiCtx = AddByImport.getUiCtx(ctx, ev);
		const ni = ctx.reg.env.nodeInfos;
		const files = await AddByImport.pickFiles(false);
		if (files) return ImportRes.doImport(files[0], {
			path: URLTREE.extractUnversionedLeafPath(ni.permaPath),
			olderResId: ni.resId,
			olderTs: ni.m
		}, ctx, uiCtx);
	}
}

const ReturnProps: Array<keyof (JDepotCidResult & Pick<JPersistMetas, 'resId' | 'path' | 'processing'>)> = ['scCidSessStatus', 'scCidSessDetails', 'resId', 'path', 'processing'];

/** Action qui affiche un dialogue d'édition des metas. */
export class EditMetasRes<C extends IDepotShortResCtx | IDepotResCtx> extends ResEditAction<C> {

	constructor(id?: string) {
		super(id || 'editMetas');
		this._label = "Modifier les métadonnées";
		this._icon = "/@skin@/store/actions/editMetas.svg";
		this._group = "edit";
		this._enablePerms = "action.store#edit.metasRes";
	}

	isVisible(ctx: C): boolean {
		if (!super.isVisible(ctx)) return false;
		return ctx.reg.env.resType.resForm("editMetas") != null;
	}

	async execute(ctx: C, ev?: Event) {
		const uiCtx = ResEditAction.getUiCtx(ctx, ev);
		const resPath = ctx.reg.env.path;
		const {EditMetasRes} = await import("back/store/dialogs/editMetasRes.js");
		let reg = ctx.reg;
		try {
			if (reg.env.nodeInfos && !(reg.env.nodeInfos as JNodeInfos).metas) {
				//nodeInfos existe (on n'est pas en création) mais metas non chargées
				//reg IDepotShortResUiEnv -> IDepotResUiEnv
				reg = reg.env.universe.newDepotResUiRegFromDepotUiReg(reg, resPath);
			}
			const ct = new EditMetasRes().initialize({reg, skinOver: "webzone:panel"});
			await ct.initializedAsync;
			if (ctx.reg.isClosed) return //racecond
			const path = reg.env.resTypes.humanPath(resPath);
			const cidMetas = await POPUP.showDialog<JCidMetas | null>(ct, uiCtx as HTMLElement, {
				titleBar: `Métadonnées de ${path}`,
				resizer: {}
			}).onNextClose();
			if (cidMetas) {
				return ResEditAction.sendCidUpdate(cidMetas, ctx, uiCtx);
			}
		} finally {
			if (reg !== ctx.reg) reg.close();
		}
	}
}


/** Affectation des roles par user sur une res. */
export class EditPermsRes<C extends IDepotResCtx> extends ResEditAction<C> {

	constructor(id?: string) {
		super(id || "editUserRoleOnRes");
		this._label = "Permissions...";
		this._description = "Définir les rôles des utilisateurs sur cette ressource ou ce dossier et ses contenus";
		this._group = "edit";
		this._icon = "/@skin@/store/actions/editPerms.svg";
		this.requireEnabledPerm(["action.store#edit.res.perms", "admin.UTNode.configRoles.byAdmin"]);
	}

	async execute(ctx: C, ev?: Event): Promise<void> {
		const reg = ctx.reg;
		const uiCtx = ResEditAction.getUiCtx(ctx, ev);
		const {ObjRolesMapEdit} = await import("back/core/dialogs/objRolesMap.js");
		const path = reg.env.resTypes.humanPath(reg.env.path);
		const objThis: C & { lastFetch: JNodeInfos } = Object.create(ctx);
		await POPUP.showDialog(new ObjRolesMapEdit<C & { lastFetch: JNodeInfos }, IDepotShortResUiEnv | IDepotResUiEnv>().initialize({
			reg,
			uiContext: "popup",
			rolesUiLayerSng: "res",
			objThis,
			objFetchRolesMap: async (objThis, pAccounts?: string[]) => {
				objThis.lastFetch = await objThis.reg.env.universe.adminUrlTree.fetchUserRolesMap(objThis.reg.env.path);
				return objThis.lastFetch.userRolesMap;
			},
			objFetchRolesMapForAccounts: async (objThis, pAccounts) => {
				const nodeInfosForAccounts = await objThis.reg.env.universe.adminUrlTree.fetchUserRolesMapForAccounts(objThis.reg.env.path, pAccounts);
				return nodeInfosForAccounts?.userRolesMap;
			},
			objEditRolesMap: async (objThis, rolesMap) => {
				const userRolesMap = Object.assign({}, objThis.lastFetch.userRolesMap);
				for (const k of Object.keys(rolesMap)) userRolesMap[k] = rolesMap[k] === null ? undefined : rolesMap[k];
				//console.log("userRolesMap::::::", userRolesMap);
				try {
					const newMetas: JCidMetas = {
						path: objThis.reg.env.path,
						olderResId: objThis.lastFetch.resId || "",
						userRolesMap
					}
					if (!newMetas.olderResId) {
						//Nécessaire si node actuel créé par défaut sans resId (folder racine ou folder auto créé)
						newMetas.processing = objThis.reg.env.universe.resTypes.defaultFolderType.prc;
					}
					const result = await ResEditAction.sendCidUpdate(newMetas, ctx, uiCtx, ["userRolesMap"]);
					if (result) objThis.lastFetch = {
						permaPath: result.path,
						resId: result.resId,
						userRolesMap: result.userRolesMap
					};
					return result?.userRolesMap || null;
				} catch (e) {
					//Erreur déjà notifée dans sendCidUpdate
					return null;
				}
			},
			addAnonymousAccount: reg.env.universe.urlTree.anonymousAccount,
			objEditRolesMapPerms: ["action.store#edit.res.perms", "admin.UTNode.configRoles.byAdmin"],
		}), reg.env.uiRoot, {
			titleBar: {barLabel: {label: `Permissions sur '${path}'`}, closeButton: {}},
			resizer: {},
			initWidth: "40em",
			initHeight: "40em"
		}).onNextClose();
	}
}


/** Action qui affiche un dialogue d'édition des metas. */
export class SwitchProcessing<C extends IDepotShortResCtx | IDepotResCtx> extends ResEditAction<C> {

	prcTarget: string

	confirmMsg: string | ((ctx: C) => string)

	constructor(id?: string) {
		super(id || 'switchPrc');
		//this._label = "Modifier les métadonnées";
		//this._icon = "/@skin@/store/actions/switchPrc.svg";
		this._group = "edit";
		this._enablePerms = "action.store#edit.metasRes";
	}

	setPrcTarget(prc: string): this {
		this.prcTarget = prc;
		return this;
	}

	setConfirmMsg(confirmMsg: string | ((ctx: C) => string)): this {
		this.confirmMsg = confirmMsg;
		return this;
	}

	async execute(ctx: C, ev?: Event) {
		const uiCtx = ResEditAction.getUiCtx(ctx, ev);
		const resPath = ctx.reg.env.path;
		const resTypeTarget = ctx.reg.env.resTypes.getResTypeByPrc(this.prcTarget);
		let file: File;
		if (!this.confirmMsg || await POPUP.confirm(typeof this.confirmMsg === "string" ? this.confirmMsg : this.confirmMsg(ctx), uiCtx)) {
			if (!resTypeTarget.prcIsNoContent) {
				const files = await AddByImport.pickFiles(false);
				if (!files) return;
				file = files[0];
			}
			const editMetas = resTypeTarget.resForm("editMetas");
			let cidMetas: JCidMetas;

			const {EditMetasRes} = await import("back/store/dialogs/editMetasRes.js");
			if (editMetas) {
				//On impose un reg dédié car on va hacker le resType.
				const reg = ctx.reg.env.universe.newDepotResUiRegFromDepotUiReg(ctx.reg, resPath);
				try {
					const ct = new EditMetasRes().initialize({
						reg,
						skinOver: "webzone:panel",
						overrideValues: (editMetas, values) => {
							values.processing = this.prcTarget;
							reg.env.universe.forceNewProcessing(reg, resTypeTarget);
							return values;
						}
					});
					await ct.initializedAsync;
					if (ctx.reg.isClosed) return //racecond
					cidMetas = await POPUP.showDialog<JCidMetas | null>(ct, uiCtx as HTMLElement, {
						titleBar: this.getLabel(ctx),
						resizer: {}
					}).onNextClose();
				} finally {
					reg.close();
				}
			} else {
				//Pas de metas
				cidMetas = {
					processing: this.prcTarget,
					path: resPath,
					olderResId: ctx.reg.env.nodeInfos.resId
				}
			}
			if (cidMetas) {
				if (file) {
					const infoBroker = ResEditAction.getInfoBroker(ctx);
					const cidResult = await ImportRes.doImport(file, cidMetas, ctx, uiCtx);
					if (cidResult && cidResult.scCidSessStatus === "commited") infoBroker?.dispatchInfo(new InfoFocusRes(cidResult.path), uiCtx);
				} else {
					return ResEditAction.sendCidUpdate(cidMetas, ctx, uiCtx);
				}
			}
		}
	}
}


/**
 * Actions : ouverture d'une vue dans un onglet
 */
export class OpenViewRes<C extends IDepotShortResCtx | IDepotResCtx> extends Action<C> {

	constructor(public subPath?: string, public directive?: string, id?: string) {
		super(id || 'openViewRes');
		this.setIcon("/@skin@/store/views/resViews/openInNewTab.svg")
		this.setLabel("Ouvrir");
		this.setDescription(Desk.electron ? "Ouvrir dans votre navigateur" : "Ouvrir dans un onglet");
		this._group = "views";
	}

	isVisible(ctx: C): boolean {
		if (ctx.reg.env.nodeInfos.trashed) return false;
		return super.isVisible(ctx);
	}

	async execute(ctx: C, ev?: Event) {
		window.open(ctx.reg.env.universe.urlTree.urlFromPath(URLTREE.appendToPath(ctx.reg.env.nodeInfos.permaPath, this.subPath), this.directive), '_blank');
	}
}

/**
 * Actions : téléchargement
 */
export class DownloadRes<C extends IDepotShortResCtx | IDepotResCtx> extends Action<C> {

	constructor(public subPath?: string, id?: string) {
		super(id || 'download');
		this.setIcon("/@skin@/store/views/resViews/downloadRes.svg")
		this.setLabel("Télécharger");
		this._group = "views";
	}

	isVisible(ctx: C): boolean {
		if (ctx.reg.env.nodeInfos.t === "moved") return false;
		return super.isVisible(ctx);
	}

	async execute(ctx: C, ev?: Event) {
		const link = <a/>;
		link.href = ctx.reg.env.universe.urlTree.urlFromPath(URLTREE.appendToPath(ctx.reg.env.nodeInfos.permaPath, this.subPath), "download&evenIfTrashed");
		link.download = ctx.reg.env.resType.resName(ctx.reg.env.nodeInfos);
		link.click();
	}
}


/** ActionWrapper qui transforme un Ctx IDepotCtx en IDepotShortResCtx si une et une seule res est sélectionnée. */
export class Depot2ResCtxAction<C extends IDepotCtx> extends ActionHackCtx<C, IDepotShortResCtx> {

	isVisible(ctx: C): boolean {
		if (ctx.resList.length !== 1) return false;
		return super.isVisible(ctx);
	}

	isEnabled(ctx: C): boolean {
		if (ctx.resList.length !== 1) return false;
		return super.isEnabled(ctx);
	}

	wrapCtx(ctx: C): IDepotShortResCtx {
		if (ctx.resList.length !== 1) return null;
		const res = ctx.resList[0];
		const type = ctx.reg.env.resTypes.getResTypeFor(res);
		return {
			reg: type.newShortResRegFromDepotReg(ctx.reg, res),
			infoBroker: ctx.infoBroker,
			emitter: ctx.emitter,
		};
	}
}


// Usage à valider...
// /** ActionWrapper qui transforme un Ctx IDepotResCtx en IDepotCtx. */
// export class Res2DepotCtxAction<C extends IDepotResCtx> extends ActionHackCtx<C, IDepotCtx> {
// 	wrapCtx(ctx: C): IDepotCtx {
// 		return {
// 			reg: ctx.reg.parentReg as IReg<IDepotUiEnv>,
// 			resList: [ctx.reg.env.nodeInfos],
// 			infoBroker: ctx.infoBroker,
// 			emitter: ctx.emitter,
// 		};
// 	}
// }
